import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import SiteHeader from "@/components/layout/SiteHeader";
import Footer from "@/components/landing/Footer";
import ProductCard from "@/components/products/ProductCard";
import { ProductImagePlaceholder } from "@/components/products/ProductImagePlaceholder";
import { getCategoryBySlug } from "@/constants/categories";
import { useProduct, useRelatedProducts } from "@/hooks/useProducts";
import { cloudinaryProductImageUrl, hasProductImage } from "@/lib/productImages";
import { getProductImages } from "@/types/product";

export const ProductDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [activeImage, setActiveImage] = useState(0);
  const [mainImageFailed, setMainImageFailed] = useState(false);
  const { data: product, isLoading, isError } = useProduct(slug);
  const { data: related = [] } = useRelatedProducts(slug, 4);

  useEffect(() => {
    setActiveImage(0);
    setMainImageFailed(false);
  }, [slug]);

  useEffect(() => {
    setMainImageFailed(false);
  }, [activeImage]);

  if (isLoading) {
    return (
      <div className="bg-white text-vg-ink relative min-h-screen">
        <div className="noise-overlay" />
        <SiteHeader />
        <main className="pt-24 sm:pt-28 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-12 max-w-[1600px] mx-auto">
          <p className="font-mono-acc text-sm uppercase tracking-[0.2em] text-vg-gray">Loading…</p>
        </main>
        <Footer />
      </div>
    );
  }

  const category = product ? getCategoryBySlug(product.categorySlug) : undefined;
  const gallery = product ? getProductImages(product) : [];
  const notFound = isError || !product || !category;

  if (notFound) {
    return (
      <div className="bg-white text-vg-ink relative min-h-screen">
        <div className="noise-overlay" />
        <SiteHeader />
        <main className="pt-24 sm:pt-28 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-12 max-w-[1600px] mx-auto">
          <h1 className="font-heading font-black text-4xl tracking-tighter">Product not found</h1>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 mt-6 font-mono-acc text-xs uppercase tracking-[0.2em] link-line"
          >
            <ArrowLeft size={14} />
            Back to products
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const mainImageUrl = cloudinaryProductImageUrl(
    gallery[activeImage] ?? product.image,
    "detail",
  );
  const showMainImage = hasProductImage(mainImageUrl) && !mainImageFailed;

  return (
    <div className="bg-white text-vg-ink relative min-h-screen">
      <div className="noise-overlay" />
      <SiteHeader />

      <main className="pt-24 sm:pt-28 pb-16 sm:pb-24 overflow-x-hidden" data-testid="product-detail-page">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 min-w-0">
          <Link
            to={`/products/${product.categorySlug}`}
            className="inline-flex items-center gap-2 font-mono-acc text-xs uppercase tracking-[0.2em] text-vg-gray hover:text-vg-ink transition-colors mb-8 sm:mb-10"
            data-testid="product-back-link"
          >
            <ArrowLeft size={14} />
            Back to {category.name}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 min-w-0"
          >
            <div className="min-w-0 space-y-4">
              <div className="img-frame img-frame-static w-full max-w-full overflow-hidden border border-black/10 aspect-square sm:aspect-[4/5] lg:aspect-square bg-[#f9fafb] relative flex items-center justify-center">
                {showMainImage ? (
                  <img
                    src={mainImageUrl}
                    alt={product.name}
                    width={800}
                    height={800}
                    fetchPriority="high"
                    className="max-w-full max-h-full w-full h-full object-contain p-4 sm:p-6"
                    data-testid="product-detail-image"
                    onError={() => setMainImageFailed(true)}
                  />
                ) : (
                  <ProductImagePlaceholder iconSize={72} showLabel className="relative inset-auto w-full h-full min-h-[280px]" />
                )}
              </div>
              {gallery.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1" data-testid="product-detail-thumbnails">
                  {gallery.map((url, index) => (
                    <button
                      key={url}
                      type="button"
                      onClick={() => setActiveImage(index)}
                      className={`shrink-0 w-16 h-16 border overflow-hidden ${
                        index === activeImage ? "border-vg-navy" : "border-black/10 opacity-70 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={cloudinaryProductImageUrl(url, "thumbnail")}
                        alt=""
                        width={64}
                        height={64}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="min-w-0 flex flex-col justify-center">
              <Link
                to={`/products/${product.categorySlug}`}
                className="font-mono-acc text-xs uppercase tracking-[0.2em] text-vg-gray hover:text-vg-cyan transition-colors w-fit"
                data-testid="product-detail-category"
              >
                {category.name}
              </Link>
              <h1
                className="font-heading font-black text-3xl sm:text-4xl md:text-6xl tracking-tighter mt-4 leading-tight"
                data-testid="product-detail-name"
              >
                {product.name}
              </h1>
            </div>
          </motion.div>

          {related.length > 0 && (
            <section className="mt-24 lg:mt-32 border-t border-black/10 pt-16" data-testid="related-products">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                <div>
                  <span className="font-mono-acc uppercase tracking-[0.2em] text-xs text-vg-gray">
                    You may also like
                  </span>
                  <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-3">
                    Related Products
                  </h2>
                </div>
                <Link
                  to={`/products/${product.categorySlug}`}
                  className="link-line font-mono-acc uppercase tracking-[0.2em] text-xs text-vg-ink"
                >
                  View all in {category.name} →
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                {related.map((item, index) => (
                  <ProductCard key={item.id} product={item} index={index} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
